// useVideoProgress.js
import { useEffect, useRef } from "react";

export default function useVideoProgress(videoRef, {
  onProgress,      // (secondsWatchedDelta, currentTime, duration) => void
  onComplete,      // () => void
  pingIntervalMs = 5000,   // throttle server pings
  completeThreshold = 0.98 // 98% watched counts as complete
} = {}) {
  const lastPingRef = useRef({ t: 0, ct: 0, sw: 0 });
  const completedRef = useRef(false);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    function secondsWatchedDelta() {
      const now = Date.now();
      const elapsed = Math.max(0, (now - lastPingRef.current.t) / 1000);
      lastPingRef.current.t = now;
      return (v && !v.paused && !v.seeking && v.readyState >= 2) ? elapsed : 0;
    }

    function maybeComplete() {
      if (completedRef.current || !Number.isFinite(v.duration) || v.duration === 0) return;
      const nearEnd =
        v.ended ||
        (v.duration > 10
          ? v.currentTime / v.duration >= completeThreshold
          : (v.duration - v.currentTime) <= 1); // for very short videos
      if (nearEnd) {
        completedRef.current = true;
        onComplete && onComplete();
      }
    }

    function handleTimeUpdate() {
      // Throttle progress pings
      const now = Date.now();
      if (now - lastPingRef.current.t >= pingIntervalMs) {
        const swDelta = secondsWatchedDelta();
        onProgress && onProgress(swDelta, Math.floor(v.currentTime), Math.floor(v.duration || 0));
      }
      maybeComplete();
    }

    function handlePlay() {
      // reset ping clock so first delta isn’t huge
      lastPingRef.current.t = Date.now();
    }

    function handlePauseOrSeeked() {
      // send a small final ping for any accrued time
      const swDelta = secondsWatchedDelta();
      if (swDelta > 0) {
        onProgress && onProgress(swDelta, Math.floor(v.currentTime), Math.floor(v.duration || 0));
      }
      maybeComplete();
    }

    v.addEventListener("timeupdate", handleTimeUpdate);
    v.addEventListener("ended", maybeComplete);
    v.addEventListener("play", handlePlay);
    v.addEventListener("pause", handlePauseOrSeeked);
    v.addEventListener("seeked", handlePauseOrSeeked);
    document.addEventListener("visibilitychange", handlePauseOrSeeked); // tab switch

    return () => {
      v.removeEventListener("timeupdate", handleTimeUpdate);
      v.removeEventListener("ended", maybeComplete);
      v.removeEventListener("play", handlePlay);
      v.removeEventListener("pause", handlePauseOrSeeked);
      v.removeEventListener("seeked", handlePauseOrSeeked);
      document.removeEventListener("visibilitychange", handlePauseOrSeeked);
    };
  }, [videoRef, onProgress, onComplete, pingIntervalMs, completeThreshold]);
}
